const STORAGE_KEY = 'safeguardpro_messages_v1';

const readMessages = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const writeMessages = (messages) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
};

const normalizeRecipients = (message) => {
  if (Array.isArray(message.recipients) && message.recipients.length) {
    return message.recipients.map((recipient) => ({
      id: recipient.id,
      name: recipient.name || 'Unknown',
      type: recipient.type || 'staff'
    }));
  }
  if (Array.isArray(message.toIds) && message.toIds.length) {
    return message.toIds.map((id, index) => ({
      id,
      name: message.toNames?.[index] || 'Unknown',
      type: 'staff'
    }));
  }
  return [];
};

const normalizeAssignment = (message) => {
  if (!message) return null;
  if (message.assignment && typeof message.assignment === 'object') {
    return {
      statusBy: message.assignment.statusBy || message.assignment.status_by || {},
      completedAtBy: message.assignment.completedAtBy || message.assignment.completed_at_by || {},
      dueDate: message.assignment.dueDate || message.assignment.due_date || null,
      assignedAt: message.assignment.assignedAt || message.assignment.assigned_at || null
    };
  }
  if (message.assignment === true) {
    return {
      statusBy: {},
      completedAtBy: {},
      dueDate: null,
      assignedAt: message.createdAt || new Date().toISOString()
    };
  }
  return null;
};

const buildAssignmentPayload = (assignment, staffRecipients) => {
  if (!assignment) return null;
  const base = typeof assignment === 'object' ? assignment : {};
  const statusBy = { ...(base.statusBy || {}) };
  staffRecipients.forEach((recipient) => {
    if (!statusBy[recipient.id]) {
      statusBy[recipient.id] = 'open';
    }
  });
  return {
    statusBy,
    completedAtBy: base.completedAtBy || {},
    dueDate: base.dueDate || null,
    assignedAt: base.assignedAt || new Date().toISOString()
  };
};

const normalizeMessage = (message) => {
  const recipients = normalizeRecipients(message);
  return {
    id: message.id || `msg-${Math.random().toString(36).slice(2, 10)}`,
    subject: message.subject || 'Message',
    body: message.body || '',
    fromId: message.fromId,
    fromName: message.fromName || 'Unknown',
    recipients,
    toIds: message.toIds || recipients.filter((recipient) => recipient.type === 'staff').map((recipient) => recipient.id),
    toNames: message.toNames || recipients.map((recipient) => recipient.name),
    createdAt: message.createdAt || new Date().toISOString(),
    readBy: message.readBy || [],
    starredBy: message.starredBy || [],
    deletedBy: message.deletedBy || [],
    references: message.references || [],
    assignment: normalizeAssignment(message),
    logStatusBy: message.logStatusBy || message.log_status_by || {},
    logCompletedAtBy: message.logCompletedAtBy || message.log_completed_at_by || {}
  };
};

const isStaffRecipient = (recipient) => recipient?.type === 'staff';

const isUserInMessage = (message, userId) =>
  message.fromId === userId ||
  (message.recipients || []).some((recipient) => isStaffRecipient(recipient) && recipient.id === userId) ||
  (message.toIds || []).includes(userId);

export default class MessageService {
  list() {
    return readMessages().map((message) => normalizeMessage(message));
  }

  getById(id) {
    return this.list().find((message) => message.id === id) || null;
  }

  enforceLimit(userId, limit = 50) {
    const messages = this.list();
    const relevant = messages.filter((message) => isUserInMessage(message, userId));
    const sorted = relevant.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const overflow = sorted.slice(limit);
    if (overflow.length === 0) return messages;
    const overflowIds = new Set(overflow.map((message) => message.id));
    const next = messages.map((message) => {
      if (!overflowIds.has(message.id)) return message;
      if (message.deletedBy.includes(userId)) return message;
      return { ...message, deletedBy: [...message.deletedBy, userId] };
    });
    writeMessages(next);
    return next;
  }

  listForUser(userId, limit = 50) {
    const messages = this.enforceLimit(userId, limit);
    return messages.filter((message) => isUserInMessage(message, userId) && !message.deletedBy.includes(userId));
  }

  listInbox(userId, limit = 50) {
    return this.listForUser(userId, limit).filter(
      (message) =>
        message.toIds.includes(userId) ||
        (message.recipients || []).some((recipient) => isStaffRecipient(recipient) && recipient.id === userId)
    );
  }

  listSent(userId, limit = 50) {
    return this.listForUser(userId, limit).filter((message) => message.fromId === userId);
  }

  send({ subject, body, fromUser, recipients = [], references = [], assignment = null }) {
    const normalizedRecipients = recipients.map((recipient) => ({
      id: recipient.id,
      name: recipient.name || 'Unknown',
      type: recipient.type || 'staff'
    }));
    const staffRecipients = normalizedRecipients.filter((recipient) => recipient.type === 'staff');
    const message = normalizeMessage({
      subject,
      body,
      fromId: fromUser.id,
      fromName: fromUser.name,
      recipients: normalizedRecipients,
      toIds: staffRecipients.map((recipient) => recipient.id),
      toNames: normalizedRecipients.map((recipient) => recipient.name),
      references,
      assignment: buildAssignmentPayload(assignment, staffRecipients)
    });
    const next = [message, ...readMessages()];
    writeMessages(next);
    return message;
  }

  markRead(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      if (message.readBy.includes(userId)) return message;
      return { ...message, readBy: [...message.readBy, userId] };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  deleteForUser(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      if (message.deletedBy.includes(userId)) return message;
      return { ...message, deletedBy: [...message.deletedBy, userId] };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  toggleStar(id, userId) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      const isStarred = message.starredBy.includes(userId);
      const starredBy = isStarred
        ? message.starredBy.filter((entry) => entry !== userId)
        : [...message.starredBy, userId];
      return { ...message, starredBy };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  updateAssignmentStatus(id, userId, status) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      const current = normalizeAssignment(message) || {
        statusBy: {},
        completedAtBy: {},
        dueDate: null,
        assignedAt: message.createdAt || new Date().toISOString()
      };
      const statusBy = { ...current.statusBy, [userId]: status };
      const completedAtBy = { ...current.completedAtBy };
      if (status === 'completed') {
        completedAtBy[userId] = new Date().toISOString();
      } else {
        delete completedAtBy[userId];
      }
      return { ...message, assignment: { ...current, statusBy, completedAtBy } };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }

  updateLogStatus(id, userId, status) {
    const messages = this.list();
    const next = messages.map((message) => {
      if (message.id !== id) return message;
      const statusBy = { ...(message.logStatusBy || {}) };
      const completedAtBy = { ...(message.logCompletedAtBy || {}) };
      statusBy[userId] = status;
      if (status === 'completed') {
        completedAtBy[userId] = new Date().toISOString();
      } else {
        delete completedAtBy[userId];
      }
      return { ...message, logStatusBy: statusBy, logCompletedAtBy: completedAtBy };
    });
    writeMessages(next);
    return next.find((message) => message.id === id) || null;
  }
}
